import { supabase } from "@/lib/supabase";
import type { Tables } from "@synkro/db";

export type OrderRow = Pick<
  Tables<"orders">,
  | "id"
  | "order_number"
  | "created_at"
  | "order_status"
  | "payment_status"
  | "fulfillment_type"
  | "total"
  | "currency"
  | "customer_name_snapshot"
  | "customer_phone_snapshot"
  | "district"
>;

const COLUMNS =
  "id, order_number, created_at, order_status, payment_status, fulfillment_type, total, currency, customer_name_snapshot, customer_phone_snapshot, district";

export interface OrdersQuery {
  organizationId: string;
  statuses?: readonly string[] | null;
  search?: string;
  page: number;
  pageSize: number;
}

export interface OrdersPage {
  rows: OrderRow[];
  total: number;
}

/**
 * Paginacion y filtros en el servidor desde el primer dia, aunque hoy haya
 * treinta pedidos. Traerlos todos y filtrar en React funciona hasta que deja
 * de funcionar, y para entonces hay que rehacer la pantalla entera.
 *
 * El filtro por organizacion no es la barrera de seguridad — eso es RLS —
 * sino lo que permite usar el indice (organization_id, created_at desc).
 */
export async function fetchOrders({
  organizationId,
  statuses,
  search,
  page,
  pageSize,
}: OrdersQuery): Promise<OrdersPage> {
  let query = supabase
    .from("orders")
    .select(COLUMNS, { count: "exact" })
    .eq("organization_id", organizationId)
    .order("created_at", { ascending: false })
    .range(page * pageSize, page * pageSize + pageSize - 1);

  if (statuses && statuses.length > 0) {
    query = query.in("order_status", statuses);
  }

  const term = search?.trim();
  if (term) {
    const escaped = term.replace(/[%,]/g, "");
    query = query.or(
      `order_number.ilike.%${escaped}%,customer_name_snapshot.ilike.%${escaped}%,customer_phone_snapshot.ilike.%${escaped}%`,
    );
  }

  const { data, error, count } = await query;
  if (error) throw error;

  return { rows: (data ?? []) as OrderRow[], total: count ?? 0 };
}

/* ------------------------------------------------------------- un pedido */

export async function fetchOrder(id: string) {
  const { data, error } = await supabase
    .from("orders")
    .select("*")
    .eq("id", id)
    .single();

  if (error) throw error;
  return data;
}

export async function fetchOrderItems(orderId: string) {
  const { data, error } = await supabase
    .from("order_items")
    .select("id, product_name_snapshot, variant_name_snapshot, sku_snapshot, unit_price, quantity, discount, total")
    .eq("order_id", orderId)
    .order("created_at");

  if (error) throw error;
  return data ?? [];
}

export async function fetchOrderHistory(orderId: string) {
  const { data, error } = await supabase
    .from("order_status_history")
    .select("id, status, notes, is_public, created_at")
    .eq("order_id", orderId)
    .order("created_at");

  if (error) throw error;
  return data ?? [];
}

export async function fetchOrderPayments(orderId: string) {
  const { data, error } = await supabase
    .from("payments")
    .select("id, payment_method_code, amount, status, reference, verified_at, created_at")
    .eq("order_id", orderId)
    .order("created_at");

  if (error) throw error;
  return data ?? [];
}

export async function fetchPaymentMethods(organizationId: string) {
  const { data, error } = await supabase
    .from("payment_methods")
    .select("code, name, requires_proof")
    .eq("organization_id", organizationId)
    .eq("active", true)
    .order("sort_order");

  if (error) throw error;
  return data ?? [];
}

/* --------------------------------------------------------------- acciones */
// Nada de UPDATE directo sobre `orders`: el panel no tiene ese permiso y no
// deberia tenerlo. Cambiar un estado mueve stock, escribe historial y puede
// disparar avisos; todo eso vive en una funcion de PostgreSQL.

export async function transitionOrder(orderId: string, status: string, notes?: string) {
  const { data, error } = await supabase.rpc("transition_order_status", {
    p_order_id: orderId,
    p_status: status,
    p_notes: notes ?? undefined,
  });
  if (error) throw error;
  return data;
}

export async function cancelOrder(orderId: string, reason: string) {
  const { data, error } = await supabase.rpc("cancel_order", {
    p_order_id: orderId,
    p_reason: reason,
  });
  if (error) throw error;
  return data;
}

export async function registerPayment(input: {
  orderId: string;
  methodCode: string;
  amount: string;
  status: string;
  reference?: string;
}) {
  const { data, error } = await supabase.rpc("register_payment", {
    p_order_id: input.orderId,
    p_method_code: input.methodCode,
    p_amount: input.amount,
    p_status: input.status,
    p_reference: input.reference || undefined,
  });
  if (error) throw error;
  return data;
}

/** Contadores de los filtros rapidos. Una sola consulta agregada. */
export async function fetchOrderCounts(
  organizationId: string,
): Promise<Record<string, number>> {
  const { data, error } = await supabase
    .from("orders")
    .select("order_status")
    .eq("organization_id", organizationId);

  if (error) throw error;

  const counts: Record<string, number> = {};
  for (const row of data ?? []) {
    counts[row.order_status] = (counts[row.order_status] ?? 0) + 1;
  }
  return counts;
}
